import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2Icon, PlusIcon } from 'lucide-react';
import { GoalCard, type Goal } from '@/components/goals/GoalCard';
import { GoalFormSheet } from '@/components/goals/GoalFormSheet';
import { GoalDetailsModal } from '@/components/goals/GoalDetailsModal';
import { useGoals, useCreateGoal, useUpdateGoal } from '@/hooks/use-goals';
import type { CreateGoalRequest, UpdateGoalRequest } from '@/lib/api/goals';

export const Route = createFileRoute('/admin/employees/$id/goals')({
  component: EmployeeGoalsPage,
});

function EmployeeGoalsPage() {
  const { id } = Route.useParams();
  const { data: goals, isLoading, error } = useGoals(id);
  const createGoalMutation = useCreateGoal(id);
  const updateGoalMutation = useUpdateGoal(id);

  const [showGoalSheet, setShowGoalSheet] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);

  const handleCreateGoal = async (data: CreateGoalRequest) => {
    await createGoalMutation.mutateAsync(data);
    setShowGoalSheet(false);
  };

  const handleUpdateGoal = async (data: UpdateGoalRequest) => {
    if (!editingGoal) return;
    await updateGoalMutation.mutateAsync({ goalId: editingGoal.id, data });
    setShowGoalSheet(false);
    setEditingGoal(null);
  };

  const handleViewGoal = (goal: Goal) => {
    setSelectedGoal(goal);
    setShowDetailsModal(true);
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setShowGoalSheet(true);
    setShowDetailsModal(false);
  };

  const handleAddGoalClick = () => {
    setEditingGoal(null);
    setShowGoalSheet(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-destructive">Failed to load goals. Please try again.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {goals && goals.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onView={handleViewGoal}
                onEdit={handleEditGoal}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No goals set for this employee</p>
              <Button onClick={handleAddGoalClick} variant="outline">
                <PlusIcon className="h-4 w-4 mr-2" />
                Create First Goal
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Goal Form Sheet */}
      <GoalFormSheet
        open={showGoalSheet}
        onOpenChange={setShowGoalSheet}
        employeeId={id}
        goal={editingGoal}
        onSubmit={editingGoal ? handleUpdateGoal : handleCreateGoal}
        isSubmitting={createGoalMutation.isPending || updateGoalMutation.isPending}
      />

      {/* Goal Details Modal */}
      <GoalDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        goal={selectedGoal}
        onEdit={handleEditGoal}
      />
    </>
  );
}
